import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Leftsidebar from "../sidebar/leftsidebar";
import LeftResponsivesidebar from "../sidebar/left-responsive";
import Headmenu from "../sidebar/headmenu";
import Loading from "../common/Loading";
import { toast } from "react-toastify";
import { getAdSet, getAllAds, getAdSetInsights, pauseAdSet, resumeAdSet, updateAdSet } from "../../hook/useAdsApi";
import AdInsightsPanel from "./AdInsightsPanel";
import {
  AD_STATUS_FILTER_TABS,
  statusBadgeClasses,
  statusCardClasses,
  getAdDisplayLabel,
  campaignStatusBadgeClasses,
  getCampaignDisplayLabel,
} from "./adsConstants";

export default function ViewAdSet() {
  const { adSetId } = useParams();
  const navigate = useNavigate();
  const [adSet, setAdSet] = useState(null);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(AD_STATUS_FILTER_TABS[0].label);
  const loadInsights = useCallback((params) => getAdSetInsights(adSetId, params), [adSetId]);

  const loadAdSet = useCallback(() => {
    setLoading(true);
    return Promise.all([getAdSet(adSetId), getAllAds(adSetId)])
      .then(([adSetResult, adsResult]) => {
        setAdSet(adSetResult);
        setAds(adsResult);
      })
      .catch(() => toast.error("Failed to load ad set", { toastId: "ad-set-load-error" }))
      .finally(() => setLoading(false));
  }, [adSetId]);

  useEffect(() => {
    loadAdSet();
  }, [loadAdSet]);

  const runAction = async (action, successMessage) => {
    setActionLoading(true);
    try {
      await action();
      toast.success(successMessage);
      await loadAdSet();
    } catch (error) {
      toast.error(error.response?.data?.message || "Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handlePause = () => runAction(() => pauseAdSet(adSetId), "Ad set paused");
  const handleResume = () => runAction(() => resumeAdSet(adSetId), "Ad set resumed");
  const handleActivate = () => runAction(() => updateAdSet(adSetId, { status: "Active" }), "Ad set activated");
  const handleComplete = () => runAction(() => updateAdSet(adSetId, { status: "Completed" }), "Ad set marked as completed");

  const filteredAds = useMemo(() => {
    const tab = AD_STATUS_FILTER_TABS.find((t) => t.label === activeTab);
    if (!tab || tab.value === null) return ads;
    return ads.filter((ad) => ad.status === tab.value);
  }, [ads, activeTab]);

  return (
    <div className="dashboard w-full min-h-screen flex">
      <Leftsidebar />
      <main className="flex-1 min-w-0 p-4 md:p-6 md:pl-8">
        <LeftResponsivesidebar />
        <Headmenu />
        <main className="flex">
          <div className="w-full max-w-3xl">
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
              <div className="flex items-center gap-3">
                <button onClick={() => navigate(-1)} className="rounded-full bg-[#e7f3ea] p-2">
                  ←
                </button>
                <h1 className="text-xl font-bold">View Ad Set</h1>
              </div>
              <div className="flex items-center gap-2">
                {adSet && adSet.status !== "Completed" && (
                  <button
                    type="button"
                    onClick={() => navigate(`/ads/ad-sets/${adSetId}/edit`)}
                    className="border-2 border-[#095228] text-[#095228] font-semibold px-4 py-2 rounded-full text-sm"
                  >
                    Edit
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => navigate(`/ads/ad-sets/${adSetId}/ads/new`)}
                  className="bg-[#095228] text-white font-semibold px-4 py-2 rounded-full text-sm"
                >
                  + Create Ad
                </button>
              </div>
            </div>
            <div className="w-full h-1 bg-[#d9d9d9] rounded-full mb-6" />

            {loading || !adSet ? (
              <div className="flex justify-center mt-16">
                <Loading />
              </div>
            ) : (
              <>
                {/* Exact colors/layout from Figma's View Ad Set screen (read-only access,
                    matched from a shared screenshot). */}
                <div className="rounded-2xl border p-4 mb-6 bg-[#EAF6EE]">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-semibold uppercase px-2 py-0.5 rounded-full ${campaignStatusBadgeClasses(adSet.status)}`}>
                      {getCampaignDisplayLabel(adSet.status)}
                    </span>
                    <span className="text-xs text-[#747474]">ID: ADSET-{adSet.id}</span>
                  </div>
                  <p className="font-bold mt-2">{adSet.name}</p>
                  <div className="flex items-center gap-2 mt-2 text-sm">
                    <span className="text-[#747474]">Target Type</span>
                    <span className="bg-[#095228] text-white px-3 py-1 rounded-full text-xs capitalize">
                      {adSet.target_type}
                    </span>
                  </div>
                  {/* Once Completed, no further status changes are allowed - matches the
                      agreed rule, enforced here since the API itself doesn't block it. */}
                  {adSet.status !== "Completed" && (
                    <div className="flex gap-2 flex-wrap mt-3 pt-3 border-t">
                      {adSet.status === "Draft" && (
                        <button
                          type="button"
                          disabled={actionLoading}
                          onClick={handleActivate}
                          className="border-2 border-[#095228] text-[#095228] font-semibold text-xs px-4 py-2 rounded-full disabled:opacity-50"
                        >
                          Activate
                        </button>
                      )}
                      {adSet.status === "Active" && (
                        <button
                          type="button"
                          disabled={actionLoading}
                          onClick={handlePause}
                          className="border-2 border-[#095228] text-[#095228] font-semibold text-xs px-4 py-2 rounded-full disabled:opacity-50"
                        >
                          Pause
                        </button>
                      )}
                      {adSet.status === "Paused" && (
                        <button
                          type="button"
                          disabled={actionLoading}
                          onClick={handleResume}
                          className="border-2 border-[#095228] text-[#095228] font-semibold text-xs px-4 py-2 rounded-full disabled:opacity-50"
                        >
                          Resume
                        </button>
                      )}
                      {(adSet.status === "Active" || adSet.status === "Paused") && (
                        <button
                          type="button"
                          disabled={actionLoading}
                          onClick={handleComplete}
                          className="border-2 border-[#747474] text-[#747474] font-semibold text-xs px-4 py-2 rounded-full disabled:opacity-50"
                        >
                          Mark as Completed
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <AdInsightsPanel load={loadInsights} />

                <h2 className="text-lg font-semibold mb-2">Your Ads</h2>
                <div className="flex gap-1.5 mb-4 bg-[#ebf8ed] rounded-full p-1 w-fit">
                  {AD_STATUS_FILTER_TABS.map((tab) => (
                    <button
                      key={tab.label}
                      type="button"
                      onClick={() => setActiveTab(tab.label)}
                      className={`px-5 py-1.5 rounded-full text-xs font-bold ${
                        activeTab === tab.label ? "bg-[#095228] text-white" : "text-[#095228]"
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                <div className="space-y-4">
                  {filteredAds.length === 0 && (
                    <p className="text-[#747474] text-sm">No ads found for this filter.</p>
                  )}
                  {filteredAds.map((ad) => {
                    const thumbnail = ad.thumbnail?.url || ad.creative_media?.[0]?.url;
                    const isRejected = ad.status === "Rejected";
                    return (
                      <div
                        key={ad.id}
                        onClick={() => navigate(`/ads/ads/${ad.id}`)}
                        className={`rounded-[22px] border p-4 cursor-pointer hover:shadow-md transition ${statusCardClasses(ad.status)}`}
                      >
                        {thumbnail && (
                          <img
                            src={thumbnail}
                            alt=""
                            className={`w-full h-20 object-cover rounded-[20px] mb-3 ${isRejected ? "opacity-60" : ""}`}
                          />
                        )}
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${statusBadgeClasses(ad.status)}`}>
                            {getAdDisplayLabel(ad.status)}
                          </span>
                          <span className="text-sm font-bold text-[#737688]">ID: AD-{ad.id}</span>
                        </div>
                        <p className="font-semibold text-[#131b2e] text-lg mt-1">{ad.headline || ad.name}</p>
                        {isRejected && (
                          <p className="text-sm font-bold text-[#ba1a1a]">
                            {ad.rejection_reason || "Policy violation"}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </main>
      </main>
    </div>
  );
}
